// src/features/messages/hooks/useMessageThread.js
import { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { messagesAPI } from '../services/messages';
import { useNotificationsContext } from '../../../context/NotificationsContext';
import { useSnackbar } from '../../../hooks/mui/useSnackBar';

const errorMessage = (error, fallback) => error?.response?.data?.message || fallback;

/**
 * One message with its comments and read receipts, plus the actions on it.
 * Every action returns the updated message from the server and refreshes
 * the app bar badge.
 *
 * With `markReadOnOpen` the message is marked read once it has loaded (the
 * thread dialog); the unread-messages dialog leaves that to its OK button.
 */
export function useMessageThread(messageId, { markReadOnOpen = false } = {}) {
  const { t } = useTranslation(['messages']);
  const { showError } = useSnackbar();
  const { refreshMessageCount } = useNotificationsContext();
  const [message, setMessage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const currentIdRef = useRef(messageId);
  const inFlightRef = useRef(null);

  const load = useCallback(async () => {
    // One load per open: a second one racing past the mark-read below would
    // come back with the new comments no longer flagged as new
    if (!messageId || inFlightRef.current === messageId) return;
    inFlightRef.current = messageId;
    setLoading(true);
    setError(null);
    try {
      const { data } = await messagesAPI.get(messageId);
      if (currentIdRef.current !== messageId) return;
      setMessage(data);
      if (markReadOnOpen && (!data.is_read || data.unread_comment_count > 0)) {
        await messagesAPI.markRead(messageId);
        refreshMessageCount();
      }
    } catch (err) {
      if (currentIdRef.current === messageId) setError(errorMessage(err, t('messages:feedback.load_error')));
    } finally {
      inFlightRef.current = null;
      if (currentIdRef.current === messageId) setLoading(false);
    }
  }, [messageId, markReadOnOpen, refreshMessageCount, t]);

  useEffect(() => {
    currentIdRef.current = messageId;
    setMessage(null);
    load();
  }, [messageId, load]);

  // Runs an action that returns the updated message
  const run = useCallback(async (action) => {
    setBusy(true);
    try {
      const { data } = await action();
      if (data && currentIdRef.current === messageId) setMessage(data);
      refreshMessageCount();
      return true;
    } catch (err) {
      showError(errorMessage(err, t('messages:feedback.action_error')));
      return false;
    } finally {
      setBusy(false);
    }
  }, [messageId, refreshMessageCount, showError, t]);

  const toggleReaction = useCallback((reaction) => {
    const mine = message?.reactions.some(r => r.reaction === reaction && r.reacted_by_me);
    return run(() => (mine
      ? messagesAPI.removeReaction(messageId, reaction)
      : messagesAPI.addReaction(messageId, reaction)));
  }, [message, messageId, run]);

  const addComment = useCallback(
    (content) => run(() => messagesAPI.addComment(messageId, content)),
    [messageId, run],
  );

  const deleteComment = useCallback(
    (commentId) => run(() => messagesAPI.deleteComment(messageId, commentId)),
    [messageId, run],
  );

  const markRead = useCallback(async () => {
    try {
      await messagesAPI.markRead(messageId);
      refreshMessageCount();
      return true;
    } catch (err) {
      showError(errorMessage(err, t('messages:feedback.action_error')));
      return false;
    }
  }, [messageId, refreshMessageCount, showError, t]);

  const deleteMessage = useCallback(async () => {
    try {
      await messagesAPI.remove(messageId);
      refreshMessageCount();
      return true;
    } catch (err) {
      showError(errorMessage(err, t('messages:feedback.action_error')));
      return false;
    }
  }, [messageId, refreshMessageCount, showError, t]);

  return {
    message,
    loading,
    busy,
    error,
    reload: load,
    toggleReaction,
    addComment,
    deleteComment,
    markRead,
    deleteMessage,
  };
}
